import {
  Alert,
  Button,
  Card,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from '@mui/icons-material/Close';

import Help from "~/components/common/Help";
import ShiftCenterConfigFormDialog from "./ShiftCenterConfigFormDialog";
import IsochroneMap from "./IsochroneMap";
import { useOrgConfig, ShiftCenter } from "~/providers/OrgConfigProvider";
import { useOrganizationalUnitsQuery } from "~/graphql/__generated__/graphql";

const TabShiftCenters = () => {
  const orgConfig = useOrgConfig();
  const { data, error } = useOrganizationalUnitsQuery();

  // For the shift center configuration dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentShiftCenter, setCurrentShiftCenter] =
    useState<ShiftCenter | null>(null);

  // New state for the isochrone map dialog
  const [isochroneDialogOpen, setIsochroneDialogOpen] = useState(false);
  const [currentIsochroneCenter, setCurrentIsochroneCenter] =
    useState<ShiftCenter | null>(null);

  const openDialog = (shiftCenter: ShiftCenter | null) => {
    setCurrentShiftCenter(shiftCenter);
    setTimeout(() => setDialogOpen(true), 0);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const openIsochrone = (shiftCenter: ShiftCenter) => {
    setCurrentIsochroneCenter(shiftCenter);
    setIsochroneDialogOpen(true);
  };

  const handleIsochroneDialogClose = () => {
    setIsochroneDialogOpen(false);
  };

  // Helper to parse the coordinate string into latitude and longitude numbers
  const parseCoordinates = (
    coordinatesStr: string
  ): { latitude: number; longitude: number } | null => {
    const parts = coordinatesStr.split(",");
    if (parts.length !== 2) return null;
    const latitude = parseFloat(parts[0].trim());
    const longitude = parseFloat(parts[1].trim());
    if (isNaN(latitude) || isNaN(longitude)) return null;
    return { latitude, longitude };
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error">Unable to load organizational units.</Alert>}
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                Shift Center
                <Help text="When a shift has a center point, visits for that shift will be optimized to be close to that point." />
              </TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Organizational Unit</TableCell>
              <TableCell>
                Drive time preview
                <Help text="Each color represents a different drive time distance (5, 10, 15, and 20 minutes.)"></Help>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(orgConfig.shiftCenters).map((center) => center && (
              <TableRow key={center.id}>
                <TableCell>
                  {center.name}
                  <IconButton size="small" onClick={() => openDialog(center)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell>
                  {center.addressStreet}
                  {center.addressLineTwo && `, ${center.addressLineTwo}`}
                  <br />
                  {center.addressCity}, {center.addressState} {center.addressZip}
                </TableCell>
                <TableCell>
                  {data?.organizationalUnit.find(unit => unit.id === center.organizationalUnitId)?.name ?? "—"}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openIsochrone(center)}>
                    <MapIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={4}>
                <Button
                  onClick={() => openDialog(null)}
                  size="small"
                  startIcon={<AddCircleOutlineIcon fontSize="small" />}
                >
                  Add New Shift Center
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      {dialogOpen && data && (
        <ShiftCenterConfigFormDialog
          onClose={handleDialogClose}
          currentShiftCenter={currentShiftCenter}
          organizationalUnits={data.organizationalUnit}
        />
      )}

      {isochroneDialogOpen && currentIsochroneCenter && (
        <Dialog
          open={isochroneDialogOpen}
          onClose={handleIsochroneDialogClose}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {currentIsochroneCenter.name} service area
            <IconButton
              aria-label="close"
              onClick={handleIsochroneDialogClose}
              style={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {(() => {
              const coords = parseCoordinates(
                currentIsochroneCenter.addressCoordinates
              );
              if (!coords) {
                return <div>Invalid coordinates</div>;
              }
              return (
                <IsochroneMap
                  latitude={coords.latitude}
                  longitude={coords.longitude}
                  mapboxgl_api_key={orgConfig.MAPBOXGL_API_KEY || ''}
                />
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
};

export default TabShiftCenters;
